import { useEffect, useState } from "react";
import { ChevronLeft, FileEdit, UserCheck, Loader2, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";
import type { AdminOrderListItem, OrderDetailsResponse } from "./types";
import type { DoctorListItem, LabTestOption, MedicineEntry, Medication } from "../../services/api/admin-order.types";
import { AdminOrderManagementService } from "../../services/api/admin-order.service";
import OrderDetailsHeader from "./OrderDetailsHeader";
import PrescriptionList from "./PrescriptionList";
import PatientOrderSidebar from "./PatientOrderSidebar";
import ConsultationForm from "./ConsultationForm";
import DoctorAssignFlow from "./DoctorAssignFlow";
import CancelOrderModal from "./CancelOrderModal";

interface GeneratePrescriptionProps {
  order: AdminOrderListItem;
  onBack: () => void;
}

const GeneratePrescription: React.FC<GeneratePrescriptionProps> = ({ order, onBack }) => {
  const [orderDetails, setOrderDetails] = useState<OrderDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<"self" | "doctor">("self");
  const [generating, setGenerating] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showPrescriptions, setShowPrescriptions] = useState(false);
  const [showDoctorInfo, setShowDoctorInfo] = useState(false);
  const [doctorName, setDoctorName] = useState("");
  const [doctorRegistrationNumber, setDoctorRegistrationNumber] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    fetchOrderDetails();
  }, [order.orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await AdminOrderManagementService.getOrderDetails(order.orderId);
      setOrderDetails(response);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePrescription = async (data: {
    doctor: DoctorListItem;
    chiefComplaints: string;
    diagnosis: string;
    labTests: LabTestOption[];
    medicines: MedicineEntry[];
    specialInstructions: string;
  }) => {
    if (!orderDetails) return;
    try {
      setGenerating(true);
      const now = new Date();
      const medications: Medication[] = data.medicines.map((m, i) => ({
        medicineName: m.medicineName,
        medicineId: m.medicineId,
        medicineSalt: m.medicineSalt,
        dosageType: m.dosageType,
        medicineNote: m.note,
        sequenceNo: i + 1,
        originalText: m.originalText,
        generatedText: m.generatedText,
        isTapering: false,
      }));

      const payload = {
        orderId: orderDetails.orderId,
        prescriptionType: "DOCTOR_GENERATED",
        doctor: {
          name: data.doctor.name,
          specializations: data.doctor.doctorSpecialization || "",
          eductation: data.doctor.doctorEducation || "",
          regNo: data.doctor.registrationNumber || "",
          phone: '',
          address: data.doctor.address || "",
        },
        patient: {
          name: orderDetails.orderedForName,
          mobile: orderDetails.orderedForPhone,
          patientAge: orderDetails.orderedForAge,
          patientGender: orderDetails.orderedForGender,
        },
        appointment: {
          appointmentId: orderDetails.orderNumber,
          appointmentDate: now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
          appointmentTime: now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
          dhrId: orderDetails.orderNumber,
        },
        chiefComplaints: data.chiefComplaints,
        diagnosis: data.diagnosis,
        testsPrescribed: data.labTests.map((t) => t.name),
        medications,
        specialInstructions: data.specialInstructions,
        note: "Medical substitution allowed whenever applicable",
        prescribedOn: now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
      };

      const response = await AdminOrderManagementService.generatePrescription(payload);
      if (response.success) {
        toast.success("Prescription generated successfully!");
        onBack();
      } else {
        toast.error(response.message || "Failed to generate prescription");
      }
    } catch (error) {
      toast.error("Failed to generate prescription");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={32} className="animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="p-6">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 bg-transparent border-none cursor-pointer mb-4 hover:text-slate-700">
          <ChevronLeft size={18} /> Back to Orders
        </button>
        <p className="text-sm text-red-500">Failed to load order details.</p>
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-50 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-auto p-5">
        <div className="flex items-center justify-between mb-4">
          <button onClick={onBack} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-transparent border-none cursor-pointer hover:text-slate-700">
            <ChevronLeft size={16} /> Back to Orders
          </button>
          <button
            onClick={() => setShowCancelModal(true)}
            className="px-3.5 py-1.5 rounded-lg text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 transition-colors cursor-pointer flex items-center gap-1.5 shadow-sm"
          >
            <AlertCircle size={14} /> Cancel Order
          </button>
        </div>

        <div className="flex gap-5">
          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Collapsible Order Details Header */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4">
              <button
                className="w-full px-5 py-3.5 flex items-center justify-between font-semibold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                onClick={() => setShowDetails(!showDetails)}
              >
                <span>Generate Rx Order Details ({orderDetails.orderNumber})</span>
                {showDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showDetails && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  <OrderDetailsHeader order={orderDetails} onUpdateSuccess={fetchOrderDetails} />
                </div>
              )}
            </div>
            {/* Collapsible Prescriptions */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4 shadow-sm">
              <button
                className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                onClick={() => setShowPrescriptions(!showPrescriptions)}
              >
                <span className="uppercase tracking-wider">Uploaded Prescriptions</span>
                {showPrescriptions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showPrescriptions && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  <PrescriptionList
                    prescriptions={orderDetails.prescriptions}
                    orderId={orderDetails.orderId}
                    onUploadSuccess={fetchOrderDetails}
                  />
                </div>
              )}
            </div>

            {/* Collapsible Doctor Information */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4 shadow-sm">
              <button
                className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                onClick={() => setShowDoctorInfo(!showDoctorInfo)}
              >
                <span className="uppercase tracking-wider">Doctor Information (Optional)</span>
                {showDoctorInfo ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showDoctorInfo && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Doctor Name</label>
                      <input
                        type="text"
                        value={doctorName}
                        onChange={(e) => setDoctorName(e.target.value)}
                        placeholder="Enter doctor's name"
                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all bg-slate-50/50"
                      />
                    </div>
                    <div>
                      <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Doctor Registration Number</label>
                      <input
                        type="text"
                        value={doctorRegistrationNumber}
                        onChange={(e) => setDoctorRegistrationNumber(e.target.value)}
                        placeholder="Enter registration number"
                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all bg-slate-50/50"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="flex rounded-full bg-slate-200 p-1 mb-5">
              <button
                className={`flex-1 py-2 rounded-full text-xs font-semibold transition-all border-none cursor-pointer ${selectedTab === "self" ? "bg-white text-slate-800 shadow-sm" : "bg-transparent text-slate-500"
                  }`}
                onClick={() => setSelectedTab("self")}
              >
                <FileEdit size={13} className="inline mr-1.5 -mt-0.5" />
                Generate Prescription By Yourself
              </button>
              <button
                className={`flex-1 py-2 rounded-full text-xs font-semibold transition-all border-none cursor-pointer ${selectedTab === "doctor" ? "bg-white text-slate-800 shadow-sm" : "bg-transparent text-slate-500"
                  }`}
                onClick={() => setSelectedTab("doctor")}
              >
                <UserCheck size={13} className="inline mr-1.5 -mt-0.5" />
                Generate Prescription By Doctor
              </button>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              {selectedTab === "self" ? (
                <ConsultationForm onGeneratePrescription={handleGeneratePrescription} generating={generating} />
              ) : (
                <DoctorAssignFlow orderDetails={orderDetails} onSuccess={onBack} />
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="w-[280px] flex-shrink-0 hidden lg:block">
            <PatientOrderSidebar
              products={orderDetails.products}
              ordernote={orderDetails.ordernote || (orderDetails as any).orderNote}
              actionButton={
                selectedTab === "self" ? (
                  <button
                    className="w-full py-2.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg transition-all border-none cursor-pointer disabled:opacity-40 flex items-center justify-center gap-2"
                    disabled={generating}
                    onClick={() => {
                      const form = document.querySelector("[data-generate-trigger]");
                      if (form) (form as HTMLButtonElement).click();
                    }}
                  >
                    {generating && <Loader2 size={14} className="animate-spin" />}
                    Generate
                  </button>
                ) : undefined
              }
            />
          </div>
        </div>
      </div>
      <CancelOrderModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        orderId={order.orderId}
        orderNumber={order.orderNumber}
        onSuccess={onBack}
      />
    </div>
  );
};

export default GeneratePrescription;
